import React, { useMemo } from 'react'
import {
  Box,
  BoxProps,
  Container,
  Heading,
  Flex,
  Text,
  useDisclosure,
  Icon,
  Link,
} from '@thirstycamel/ui'
import { useQuery } from 'react-query'

import { useStore } from '../../store/hooks'
import { backend } from '../../utils/backend'
import { cmsUseNew } from '../../utils/cms'
import { fetchGraphQL } from '../../utils/graphql-fetch'
import { SearchTagsDocument, SearchTagsQuery } from '../../../generated'

const handleSortRandom = () => 0.5 - Math.random()

interface CoreSearchTag {
  id: string
  title: string
  searchTerm: string
}

export interface TrendingKeywordsProps extends BoxProps {}

const TrendingKeywordsCore = ({ ...restProps }: TrendingKeywordsProps) => {
  const location = useStore(s => s.location)
  const selectedStore = useStore(s => s.store.selectedStore)
  const region = selectedStore?.region || location.region || 'VIC'
  const { isOpen, onToggle } = useDisclosure()

  const { data } = useQuery<CoreSearchTag[]>(
    ['search-tags/public', region],
    () => backend<CoreSearchTag[]>('search-tags/public', { params: { region } }),
    { staleTime: 30000 },
  )

  const keywords = useMemo(
    () => (data ? [...data].sort(handleSortRandom).slice(0, 5) : null),
    [data],
  )

  if (!keywords?.length) return null

  return (
    <TrendingKeywordsLayout
      keywords={keywords}
      isOpen={isOpen}
      onToggle={onToggle}
      {...restProps}
    />
  )
}

const TrendingKeywordsWordPress = ({ ...restProps }: TrendingKeywordsProps) => {
  const location = useStore(s => s.location)
  const selectedStore = useStore(s => s.store.selectedStore)
  const region = selectedStore?.region || location.region || 'VIC'
  const { isOpen, onToggle } = useDisclosure()

  const { data } = useQuery<SearchTagsQuery>(
    ['api/cms', SearchTagsDocument, { first: 5, region }],
    fetchGraphQL,
    { staleTime: 30000 },
  )

  const keywords = useMemo(
    () =>
      data?.searchTags?.edges
        ?.map(edge => ({
          title: edge?.node?.title || '',
          searchTerm: edge?.node?.acf?.searchTerm || edge?.node?.title || '',
        }))
        .sort(handleSortRandom),
    [data?.searchTags?.edges],
  )

  if (!keywords?.length) return null

  return (
    <TrendingKeywordsLayout
      keywords={keywords}
      isOpen={isOpen}
      onToggle={onToggle}
      {...restProps}
    />
  )
}

function TrendingKeywordsLayout({
  keywords,
  isOpen,
  onToggle,
  ...restProps
}: {
  keywords: { title: string; searchTerm: string }[]
  isOpen: boolean
  onToggle: () => void
} & BoxProps) {
  return (
    <Box bg="gray.100" borderTop="1px" borderColor="gray.200" pos="relative" {...restProps}>
      <Container
        display="flex"
        flexDirection={['column', , 'row']}
        alignItems="center"
        justifyContent={['center', , 'flex-start']}
        h={[, , '5rem']}
      >
        <Flex
          as="button"
          px={10}
          pos={[, , 'relative']}
          align="center"
          justify={['center', , 'flex-start']}
          h={['4rem', , '100%']}
          w={['100%', , 'auto']}
          onClick={onToggle}
        >
          <Box
            pos="absolute"
            w="100vw"
            h={['4rem', , 'auto']}
            left={[0, , 'auto']}
            right={0}
            top={0}
            bottom={0}
            bg="green.500"
          />
          <Box position="relative">
            <Heading
              textTransform="uppercase"
              textAlign={['center', , 'right']}
              fontSize="xs"
              position="relative"
            >
              # Popular
              <Box display={['inline', , 'block']} mx="0.1rem" />
              Thirsts
            </Heading>
            <Icon
              name={isOpen ? 'chevron-up' : 'chevron-down'}
              color="black"
              display={[, , 'none']}
              position="absolute"
              top="50%"
              left="110%"
              transform="translateY(-50%)"
              fontSize="xl"
            />
          </Box>
        </Flex>

        <Flex
          justify="space-between"
          align="center"
          pl={5}
          py={[5, , 0]}
          flex="1 1 0"
          textAlign="center"
          display={[isOpen ? null : 'none', , 'flex']}
        >
          {keywords.map(node => {
            const query: Record<string, string> = {}
            if (node.searchTerm?.includes?.(':')) {
              node.searchTerm.split('|').forEach(section => {
                const [key, value] = section?.split?.(':') || []
                if (key && value) query[key] = value
              })
            } else {
              query.search = node.searchTerm || node.title
            }

            return (
              <Link href={{ pathname: '/search', query }} key={node.title}>
                <Text mx={2} my={[3, , 0]}>
                  #{node.title}
                </Text>
              </Link>
            )
          })}
        </Flex>
      </Container>
    </Box>
  )
}

export const TrendingKeywords = (props: TrendingKeywordsProps) =>
  cmsUseNew() ? <TrendingKeywordsCore {...props} /> : <TrendingKeywordsWordPress {...props} />

export default TrendingKeywords
